import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { LINE_STATUSES, ORDER_STATUSES } from '../firebase/schema';
import { lineHasBeenOrdered } from './lineCutStatus';
import { activityLogAppend } from './lineActivityLog';

/**
 * Mark selected lines as ordered and promote parent orders when every line has left Ready To Order.
 */
export async function markLinesAsOrdered(db, { selectedLineIds, linesByOrder, lineStatusById = {} }) {
  if (!selectedLineIds?.length) return { updatedLineIds: [], updatedOrderIds: [] };

  const updateLinePromises = selectedLineIds.map(async (lineId) => {
    const lineRef = doc(db, 'orderLines', lineId);
    const fromStatus = lineStatusById[lineId] || LINE_STATUSES.READY_TO_ORDER;
    await updateDoc(lineRef, {
      status: LINE_STATUSES.ORDERED,
      orderedAt: serverTimestamp(),
      ...activityLogAppend('Marked as ordered', {
        from: fromStatus,
        to: LINE_STATUSES.ORDERED,
      }),
    });
  });

  const orderIdsToCheck = new Set();
  for (const [orderId, lines] of Object.entries(linesByOrder)) {
    if (lines.some((line) => selectedLineIds.includes(line.id))) {
      orderIdsToCheck.add(orderId);
    }
  }

  await Promise.all(updateLinePromises);

  const updatedOrderIds = [];
  for (const orderId of orderIdsToCheck) {
    const readyLines = linesByOrder[orderId] || [];
    const allReadyLinesSelected =
      readyLines.length > 0 && readyLines.every((line) => selectedLineIds.includes(line.id));

    if (!allReadyLinesSelected) continue;

    const allLinesSnap = await getDocs(
      query(collection(db, 'orderLines'), where('orderId', '==', orderId))
    );
    const allLines = allLinesSnap.docs.map((d) => d.data());
    if (allLines.length === 0) continue;

    const everyLineOrderedOrLater = allLines.every((line) => lineHasBeenOrdered(line.status));
    if (!everyLineOrderedOrLater) continue;

    await updateDoc(doc(db, 'orders', orderId), {
      status: ORDER_STATUSES.ORDERED,
      orderedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    updatedOrderIds.push(orderId);
  }

  return { updatedLineIds: selectedLineIds, updatedOrderIds };
}

/** Close out orders stuck in Ready To Order with no remaining ready lines. */
export async function reconcileStuckReadyToOrderOrders(db, orders, linesByOrder) {
  const stuckOrders = orders.filter((order) => (linesByOrder[order.id] || []).length === 0);
  if (!stuckOrders.length) return;

  await Promise.all(
    stuckOrders.map(async (order) => {
      const allLinesSnap = await getDocs(
        query(collection(db, 'orderLines'), where('orderId', '==', order.id))
      );
      const allLines = allLinesSnap.docs.map((d) => d.data());
      if (allLines.length === 0) return;
      if (!allLines.every((line) => lineHasBeenOrdered(line.status))) return;

      await updateDoc(doc(db, 'orders', order.id), {
        status: ORDER_STATUSES.ORDERED,
        orderedAt: order.orderedAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    })
  );
}
